import React, { useEffect, useState } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { createArticle, updateArticle } from "../../../Api/Speakers/Articles";

const FormError = ({ errorMessage }) => {
  return errorMessage ? (
    <div className="text-danger mt-1">{errorMessage}</div>
  ) : null;
};

const AddBlogModal = ({ show, onClose, onSuccess, handleSubmit, editMode, initialData }) => {
  const { t } = useTranslation();
  
  // حالة البيانات
  const [formData, setFormData] = useState({
    arabicTitle: "",
    englishTitle: "",
    arabicDescription: "",
    englishDescription: "",
    link: "",
    thumbnailImagePath: null,
    articleType: "",
    tags: [],
    publishToPublic: true,
    isExternalContent: false,
  });

  const [errors, setErrors] = useState({});

  // عند فتح المودال، حمّل البيانات إذا كان تعديل
  useEffect(() => {
    if (editMode && initialData) {
      setFormData({
        arabicTitle: initialData.arabicTitle || "",
        englishTitle: initialData.englishTitle || "",
        arabicDescription: initialData.arabicDescription || "",
        englishDescription: initialData.englishDescription || "",
        link: initialData.link || "",
        thumbnailImagePath: null, // لا يمكن ملء input type="file"
        articleType: initialData.articleType || "",
        tags: initialData.tags || [],
        publishToPublic: initialData.publishToPublic ?? true,
        isExternalContent: initialData.isExternalContent ?? false,
      });
    } else {
      setFormData({
        arabicTitle: "",
        englishTitle: "",
        arabicDescription: "",
        englishDescription: "",
        link: "",
        thumbnailImagePath: null,
        articleType: "",
        tags: [],
        publishToPublic: true,
        isExternalContent: false,
      });
    }
  }, [editMode, initialData, show]);

  const handleChange = (e) => {
    const { name, value, type, checked, files } = e.target;

    if (type === "file") {
      if (files && files[0]) {
        setFormData((prev) => ({ ...prev, [name]: files[0] }));
      }
    } else if (type === "checkbox") {
      setFormData((prev) => ({ ...prev, [name]: checked }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleTagsChange = (e) => {
    const value = e.target.value;
    setFormData((prev) => ({
      ...prev,
      tags: value.split(",").map((tag) => tag.trim()),
    }));
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
  
    const form = new FormData();
    Object.entries(formData).forEach(([key, value]) => {
      // تجاهل إرسال الرابط إذا كانت المقالة غير خارجية
      if (key === "link" && !formData.isExternalContent) return;
  
      if (Array.isArray(value)) {
        value.forEach((item, index) => form.append(`${key}[${index}]`, item));
      } else {
        form.append(key, value);
      }
    });
  
    try {
      if (editMode && initialData?.id) {
        await updateArticle(initialData.id, form);
        toast.success("تم التعديل بنجاح");
      } else {
        await createArticle(form);
        toast.success("تمت الإضافة بنجاح");
      }
  
      onSuccess();
      onClose();
    } catch (error) {
      console.log("Form Data before sending:", formData);
  
      console.error("Error submitting article:", error);
  
      if (error.errors) {
        const normalizedErrors = {};
        Object.entries(error.errors).forEach(([key, val]) => {
          const camelKey = key.charAt(0).toLowerCase() + key.slice(1);
          normalizedErrors[camelKey] = val;
        });
  
        setErrors(normalizedErrors);
  
        Object.values(normalizedErrors).forEach((errMsgs) => {
          errMsgs.forEach((msg) => toast.error(msg));
        });
      } else {
        toast.error(error.detail || "حدث خطأ أثناء الحفظ");
      }
    }
  };

  return (
    <div>
      <Modal show={show} onHide={onClose} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>{t("AddArticle.addNewArticle")}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleFormSubmit}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>{t("AddArticle.arabicTitle")}</Form.Label>
              <Form.Control
                type="text"
                name="arabicTitle"
                value={formData.arabicTitle}
                onChange={handleChange}
                // isInvalid={!!errors.arabicTitle}
              />
              {/* <FormError errorMessage={errors.arabicTitle} /> */}
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>{t("AddArticle.englishTitle")}</Form.Label>
              <Form.Control
                type="text"
                name="englishTitle"
                value={formData.englishTitle}
                onChange={handleChange}
                // isInvalid={!!errors.englishTitle}
              />
              {/* <FormError errorMessage={errors.englishTitle} /> */}
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>{t("AddArticle.arabicContent")}</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="arabicDescription"
                value={formData.arabicDescription}
                onChange={handleChange}
                // isInvalid={!!errors.arabicDescription}
              />
              {/* <FormError errorMessage={errors.arabicDescription} /> */}
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>{t("AddArticle.englishContent")}</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="englishDescription"
                value={formData.englishDescription}
                onChange={handleChange}
                // isInvalid={!!errors.englishDescription}
              />
              {/* <FormError errorMessage={errors.englishDescription} /> */}
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                name="isExternalContent"
                label={t("AddArticle.externalArticle")}
                checked={formData.isExternalContent}
                onChange={handleChange}
              />
            </Form.Group>

            {formData.isExternalContent && (
              <Form.Group className="mb-3">
                <Form.Label>{t("AddArticle.articleURL")}</Form.Label>
                <Form.Control
                  type="text"
                  name="link"
                  value={formData.link}
                  onChange={handleChange}
                  // isInvalid={!!errors.link}
                />
                {/* <FormError errorMessage={errors.link} /> */}
              </Form.Group>
            )}

            <Form.Group className="mb-3">
              <Form.Label>{t("AddArticle.ThumbnailImagePath")}</Form.Label>
              <Form.Control
                type="file"
                name="thumbnailImagePath"
                accept="image/*"
                onChange={handleChange}
                // isInvalid={!!errors.thumbnailImagePath}
              />
              {/* <FormError errorMessage={errors.thumbnailImagePath} /> */}
            </Form.Group>

            {editMode && initialData?.thumbnailImagePath && (
              <div className="mb-3">
                <img
                  src={initialData.thumbnailImagePath}
                  alt="thumbnail"
                  style={{ maxWidth: "150px", maxHeight: "150px", borderRadius: "8px" }}
                />
              </div>
            )}

            <Form.Group className="mb-3">
              <Form.Label>{t("AddArticle.articleType")}</Form.Label>
              <Form.Control
                type="text"
                name="articleType"
                value={formData.articleType}
                onChange={handleChange}
                // isInvalid={!!errors.articleType}
              />
              {/* <FormError errorMessage={errors.articleType} /> */}
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>{t("AddArticle.tags")}</Form.Label>
              <Form.Control
                type="text"
                name="tags"
                value={formData.tags.join(", ")}
                onChange={handleTagsChange}
                // isInvalid={!!errors.tags}
              />
              {/* <FormError errorMessage={errors.tags} /> */}
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                name="publishToPublic"
                label={t("AddArticle.publishToPublic")}
                checked={formData.publishToPublic}
                onChange={handleChange}
              />
            </Form.Group>
          </Modal.Body>

          <Modal.Footer>
            <Button variant="secondary" onClick={onClose}>
              {t("common.cancel")}
            </Button>
            <Button variant="primary" type="submit">
              {t("common.save")}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
      <ToastContainer />
    </div>
  );
};

export default AddBlogModal;
